import { request } from './client'

/* ============================================================================
   W-127, THE OTHER HALF — one room at a time.

   The spreadsheet is how five hundred rooms arrive on a Thursday before go-live. This
   is every day after that: 305 is renumbered, a suite is split, a room is knocked
   through into the corridor and stops existing.

   TWO WORDS THAT MUST NOT BE CONFUSED, here or on the screen:

     OUT OF SERVICE — the room exists and is shut for repair. It lives on the room
     board, it has its own reason and its own audit trail, and a supervisor sets it.

     OUT OF THE HOTEL — the room is not part of the hotel any more. It is on no board
     but this one, and only an administrator can do it.

   Two ways to switch a room off, in a product where nobody can tell which one they are
   looking at, is the defect this vocabulary exists to prevent.
   ========================================================================= */

/** What an administrator did to a room. The server's word. */
export type RoomAdminAction = 'CREATED' | 'RENUMBERED' | 'MOVED' | 'REMOVED' | 'RESTORED'

export interface RoomAdminRow {
  roomId: string
  roomNumber: string
  floorId: string
  floor: string
  /** Which building that floor is in. Reached through the floor on the server, sent flat
   *  here so the table can sort on it without a second lookup per row. */
  buildingId: string
  building: string
  roomTypeId: string
  typeCode: string
  typeName: string
  typeNameAr: string | null
  /** Whether the room is part of the hotel at all. NOT "in service". */
  inTheHotel: boolean
  /** Why it was taken out, derived from the latest removal rather than stored. */
  removedReason: string | null
  removedAtUtc: string | null
  /** Whether there is a live card in the room — decides whether a renumbering costs
      a reprint, which is the one consequence of this screen that happens off it. */
  hasLiveCard: boolean

  /* ── THE OTHER SIDE OF THE LINE: HOW THIS ROOM IS CLEANED ──────────────────
     Everything above comes from the property system. These are ours, and they are
     what decides whether the room can be given to anybody at all. */

  /** The TEMPLATE, not a version. The version a task uses is resolved when the task is
      raised, so publishing a new list reaches every room at once. */
  departureTemplateId: string | null
  stayoverTemplateId: string | null
  /** **HOW LONG A DEPARTURE CLEAN TAKES IN THIS ROOM, IN MINUTES.** The one number the
      hotel states per room — CQ-133 — and the thing every other figure on this screen is
      derived from. ZERO MEANS UNSET, not instant. */
  cleaningMinutes: number
  /** What that costs a lane, ALREADY DIVIDED BY THE PROPERTY'S DIVISOR AND SENT.
   *
   *  Not computed here from `cleaningMinutes / board.minutesPerCredit`, even though both
   *  are on this page and it would be one line. The assignment board and this screen must
   *  not be able to arrive at two different numbers from one fact, and a rounding rule
   *  expressed twice is a rounding rule that eventually differs in the second decimal —
   *  which is where a supervisor's lane total stops adding up. */
  cleaningCredits: number
  deepClean: DeepCleanFrequency
  cleaningNotes: string | null
  /** Which rules are missing, FROM THE SERVER.

      Not derived here, and that is the point: the same function that refuses the
      assignment decides what the row says, so a room this screen draws as ready is a
      room a supervisor can actually hand out. A copy of the rule on the client would be
      a second opinion, and the day the two differ is the day somebody is told a room is
      fine by the screen and not fine by the board. */
  missingCleaningRules: MissingCleaningRule[]
}

/** How often a room gets the clean that is not part of the daily cycle. `NONE` is an
 *  answer somebody gave — a room in a closed wing — and not a gap. */
export type DeepCleanFrequency =
  | 'NONE' | 'MONTHLY' | 'QUARTERLY' | 'TWICE_YEARLY' | 'YEARLY'

export type MissingCleaningRule = 'DEPARTURE_TEMPLATE' | 'STAYOVER_TEMPLATE' | 'MINUTES'

/** A checklist a room can be cleaned to, with the version that is LIVE today.
 *
 *  The pack's table reads "Departure clean v7". The number is drawn beside the name and
 *  is NOT what the room stores — see `departureTemplateId`. */
export interface AdminChecklistTemplate {
  templateId: string
  code: string
  name: string
  /** Null until somebody types one on W-126. The screen falls back to the English name
   *  rather than a blank — an untranslated template should read as untranslated. */
  nameAr: string | null
  /** Null when nothing has been published yet: the template exists and is choosable, and
      a room pointed at it has no list until somebody publishes one. Drawn rather than
      hidden, or a template an administrator just created is invisible here. */
  liveVersionNumber: number | null
}

export interface AdminFloor {
  floorId: string
  buildingId: string
  code: string
  sortOrder: number
  rooms: number
}

export interface AdminBuilding {
  buildingId: string
  code: string
  name: string
  nameAr: string | null
  sortOrder: number
  /** How many floors hang off it — what makes "delete" visibly not a thing to offer. */
  floors: number
}

export interface AdminRoomType {
  roomTypeId: string
  code: string
  name: string
  nameAr: string | null
  cleanMinutes: number
  rooms: number
  /**
   * HOW MANY PEOPLE A ROOM OF THIS TYPE SLEEPS. Sprint 49 — every room in both hotels
   * sleeps two or four, and this is that fact as a number rather than as an adjective.
   */
  capacity: number
  /** What "Apply the Twin defaults" applies. COPIED onto a room, never read through —
      a room whose rules pointed at its type would change how four hundred rooms are
      cleaned the moment somebody edited the type, mid-shift. */
  defaultDepartureTemplateId: string | null
  defaultStayoverTemplateId: string | null
  /* THERE WAS A `defaultCleaningCredits` HERE. It was a second number for the fact
     `cleanMinutes` above already held — defect #109 — and it is gone. "Apply the Twin
     defaults" copies the MINUTES. */
  defaultDeepClean: DeepCleanFrequency
}

export interface RoomAdminBoard {
  rooms: RoomAdminRow[]
  /** In their own order; `floors` is ordered by building and then by floor. */
  buildings: AdminBuilding[]
  floors: AdminFloor[]
  roomTypes: AdminRoomType[]
  templates: AdminChecklistTemplate[]
  /** **HOW MANY MINUTES MAKE ONE CREDIT AT THIS PROPERTY.** Sent because the screen both
   *  shows it — so a supervisor reading "45 min · 2.25" can see where the second number
   *  came from — and sets it. Twenty by default, which is the ratio the approved pack was
   *  already using in all four of its task types without saying so. */
  minutesPerCredit: number
}

export interface CleaningRulesSaved {
  rooms: RoomAdminRow[]
  /** How many rooms this action UNBLOCKED — counted before and after, so a bulk apply
      over forty rooms that were already fine reports 0 rather than 40. */
  nowAssignable: number
}

export interface RoomSaved {
  room: RoomAdminRow
  /** Null when the save changed nothing — somebody pressed the button twice. */
  action: RoomAdminAction | null
  cardNeedsReprinting: boolean
  convertedFromEasternDigits: boolean
}

export const fetchRoomAdmin = (propertyId: string, signal?: AbortSignal) =>
  request<RoomAdminBoard>(`/properties/${propertyId}/rooms/admin`, signal ? { signal } : {})

/**
 * CREATING AND EDITING ARE ONE CALL, told apart by whether `roomId` is there.
 *
 * Two calls would be two validations, and the day they disagree is the day the create
 * path accepts a room number the edit path refuses — leaving somebody with a room they
 * cannot subsequently change.
 */
export const saveRoom = (
  propertyId: string,
  room: { roomId?: string; roomNumber: string; floorId: string; roomTypeId: string },
) => request<RoomSaved>(`/properties/${propertyId}/rooms/admin`, { method: 'POST', body: room })

export const setRoomInHotel = (
  propertyId: string, roomId: string, inTheHotel: boolean, reason: string,
) => request<RoomAdminRow>(`/properties/${propertyId}/rooms/${roomId}/in-hotel`, {
  method: 'POST',
  body: { inTheHotel, reason },
})

/** A FLOOR BELONGS TO A BUILDING, so the building is a required argument rather than an
 *  optional one. An omitted building on the server binds to an empty id and is refused by
 *  name — but a client that can forget it is a client that will. */
export const addFloor = (propertyId: string, buildingId: string, code: string) =>
  request<AdminFloor>(`/properties/${propertyId}/floors`, {
    method: 'POST',
    body: { buildingId, code },
  })

/**
 * EDITING A KIND OF ROOM. Sprint 49 — the answer to "build the room type details".
 *
 * PUT, and the code is not in the body. A type is CREATED by the hotel's own room sheet:
 * a type invented here under a code the next upload does not use would split four hundred
 * rooms across two types, silently, with both looking perfectly valid.
 *
 * `capacity` is the reason this call exists. How many people a room sleeps was not
 * editable anywhere before Sprint 49, because it did not exist — it was inside the
 * English word "Twin" and nowhere else.
 */
export const saveRoomType = (
  propertyId: string,
  roomTypeId: string,
  type: {
    name: string
    nameAr?: string | null
    capacity: number
    cleanMinutes: number
    bedConfiguration?: string | null
  },
) => request<AdminRoomType>(`/properties/${propertyId}/room-types/${roomTypeId}`, {
  method: 'PUT',
  body: type,
})

export const addBuilding = (
  propertyId: string,
  building: { code: string; name: string; nameAr?: string },
) => request<AdminBuilding>(`/properties/${propertyId}/buildings`, {
  method: 'POST',
  body: building,
})

/**
 * SETTING HOW A ROOM IS CLEANED. One call for one room and for four hundred.
 *
 * "Apply the Twin defaults" and "change this room's departure list" are the same write
 * at different scales, and two calls would be two validations that eventually disagree.
 *
 * EVERY FIELD IS OPTIONAL AND OMITTING ONE LEAVES IT ALONE — the screen sends what it
 * changed, so editing the minutes on one row does not blank its notes. Clearing a
 * template is asked for in words, because clearing by omission would happen on every
 * partial save, and clearing a template takes a room out of service without saying so.
 */
export const setCleaningRules = (
  propertyId: string,
  change: {
    roomIds: string[]
    departureTemplateId?: string | null
    stayoverTemplateId?: string | null
    minutes?: number
    deepClean?: DeepCleanFrequency
    notes?: string
    clearTemplates?: boolean
  },
) => request<CleaningRulesSaved>(`/properties/${propertyId}/rooms/cleaning-rules`, {
  method: 'POST', body: change,
})

/** A checklist's name in the reader's language, falling back to the English. */
export const templateName = (t: AdminChecklistTemplate, locale: 'en' | 'ar') =>
  locale === 'ar' ? t.nameAr ?? t.name : t.name

/**
 * **CHANGING WHAT ONE CREDIT IS WORTH, FOR THE WHOLE PROPERTY.**
 *
 * ITS OWN CALL, NOT A FIELD ON `setCleaningRules`, because it is a different act at a
 * different scale: setting a room's minutes changes one room, and this re-prices every lane
 * in the hotel at once. One save that could do both is a save where somebody adjusts a
 * suite and re-balances the building.
 *
 * Returns THE WHOLE BOARD rather than an acknowledgement — every row's credit figure has
 * just changed, and a screen that patched one field would be showing old arithmetic beside
 * new.
 */
export const setMinutesPerCredit = (propertyId: string, minutes: number) =>
  request<RoomAdminBoard>(`/properties/${propertyId}/minutes-per-credit`, {
    method: 'POST', body: { minutes },
  })
